import { useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, BellIcon, ListIcon, ArrowsClockwiseIcon, GlobeIcon, CaretRightIcon, SignOutIcon } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import CompanySwitcher from "@/components/CompanySwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useSidebar } from "@/contexts/SidebarContext";

// Mapa ruta → label para breadcrumb
const ROUTE_LABELS = {
  "/dashboard": "Dashboard",
  "/productos": "Productos",
  "/productos/lista-precios": "Lista de precios",
  "/inventario/bodegas": "Bodegas",
  "/inventario/transferencias": "Transferencias",
  "/inventario/alertas": "Alertas de vencimiento",
  "/inventario/ajustes": "Ajustes de inventario",
  "/inventario/doble-lectura": "Doble lectura del costo",
  "/clientes": "Clientes",
  "/clientes/bloqueos": "Bloqueos de crédito",
  "/clientes/importar": "Importar clientes",
  "/rtc": "Representantes RTC",
  "/compras": "Compras",
  "/proveedores": "Proveedores",
  "/cxp": "Cuentas por Pagar",
  "/auto-oc": "Auto-OC",
  "/importaciones": "Importaciones",
  "/contabilidad": "Contabilidad",
  "/tesoreria": "Tesorería",
  "/gasto-distribuido": "Gasto distribuido",
  "/depositos": "Depósitos",
  "/notas-credito": "Notas de crédito de compra",
  "/sar": "Cumplimiento SAR",
  "/partidas-gasto": "Cuentas de gasto",
  "/prestamos": "Préstamos",
  "/ventas": "Ventas y Facturación",
  "/cxc": "Pagos y Cobranza",
  "/comisiones": "GRATIVA RTC · Gratificación Variable",
  "/calendario": "Calendario",
  "/reportes": "Reportes",
  "/admin": "Panel Admin",
  "/empresas": "Empresas",
  "/auditoria": "Auditoría",
  "/configuracion": "Configuración",
  "/permisos": "Permisos",
  "/viaticos": "Viáticos y Gastos",
  "/rrhh": "Recursos Humanos",
  "/formularios": "Formularios",
};

function Breadcrumb({ pathname }) {
  if (pathname === "/") return null;
  // Path parents
  const parts = pathname.split("/").filter(Boolean);
  const trail = [];
  for (let i = 0; i < parts.length; i++) {
    const path = "/" + parts.slice(0, i + 1).join("/");
    if (ROUTE_LABELS[path]) trail.push({ label: ROUTE_LABELS[path], path });
  }
  if (trail.length === 0) return null;
  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-text-muted" data-testid="breadcrumb">
      <span>AGROPORT</span>
      {trail.map((t) => (
        <span key={t.path} className="inline-flex items-center gap-1.5">
          <CaretRightIcon size={10} className="text-text-muted/60" />
          <span className={`${t === trail[trail.length - 1] ? "text-text-primary" : ""}`}>{t.label}</span>
        </span>
      ))}
    </div>
  );
}

export default function Header({ title, subtitle }) {
  const { toggle } = useSidebar();
  const { logout } = useAuth();
  const loc = useLocation();
  return (
    <header
      className="h-16 sticky top-0 z-30 backdrop-blur-xl bg-sidebar/70 border-b border-border flex items-center px-4 md:px-8 gap-3"
      data-testid="app-header"
    >
      <button
        onClick={toggle}
        data-testid="sidebar-toggle-btn"
        className="icon-btn flex-shrink-0"
        title="Mostrar / ocultar menú"
        aria-label="Mostrar u ocultar el menú"
      >
        <ListIcon size={18} weight="bold" />
      </button>

      {/* Title + breadcrumb */}
      <div className="flex-1 min-w-0">
        <Breadcrumb pathname={loc.pathname} />
        <h1 className="font-heading text-base sm:text-lg font-bold text-text-primary leading-tight truncate mt-0.5" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="text-[11px] font-medium text-text-muted mt-0.5 truncate hidden md:block">{subtitle}</p>}
      </div>

      {/* Right cluster */}
      <div className="flex items-center gap-1.5 shrink-0">
        <div className="relative hidden xl:block mr-2">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            data-testid="global-search"
            placeholder="Buscar…"
            className="bg-app border border-border/60 rounded-lg pl-9 pr-3 py-2 text-xs w-52 placeholder:text-text-muted focus:border-agro-green"
          />
        </div>
        <CompanySwitcher />
        <ThemeToggle />
        <button
          onClick={() => window.location.reload()}
          className="icon-btn hidden sm:inline-flex"
          data-testid="refresh-btn"
          title="Refrescar"
          aria-label="Refrescar"
        >
          <ArrowsClockwiseIcon size={16} />
        </button>
        <button className="icon-btn relative hidden sm:inline-flex" data-testid="notifications-btn" title="Notificaciones" aria-label="Notificaciones">
          <BellIcon size={16} />
          <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-agro-yellow" />
        </button>
        <button className="icon-btn hidden md:inline-flex" data-testid="language-btn" title="Idioma" aria-label="Idioma">
          <GlobeIcon size={16} />
        </button>
        {/* Cerrar sesión: siempre visible en la barra superior (teléfono y computadora) */}
        <button onClick={logout} className="icon-btn hover:text-agro-yellow" data-testid="header-logout-btn" title="Cerrar sesión" aria-label="Cerrar sesión">
          <SignOutIcon size={16} />
        </button>
      </div>
    </header>
  );
}
